import { Navigate, useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { LanguageRoute } from "@/components/LanguageRoute";
import { supabase } from "@/integrations/supabase/client";
import { checkIsPlatformAdmin } from "@/lib/platformAdmin";
import { MFA_REQUIRED_EVENT, mfaStep } from "@/lib/admin/mfa";
import { AdminMfa } from "./AdminMfa";

type State = "loading" | "admin" | "denied" | "enroll" | "verify";

/** The admin's next step: in, set up the authenticator, or enter its code. */
async function adminState(force?: "verify"): Promise<State> {
  if (!(await checkIsPlatformAdmin(supabase))) return "denied";
  const [{ data: aal, error: aalError }, { data: factors, error: factorError }] = await Promise.all(
    [supabase.auth.mfa.getAuthenticatorAssuranceLevel(), supabase.auth.mfa.listFactors()]
  );
  if (aalError || factorError) return "denied";
  const verified = factors?.totp.length ?? 0;
  // An admin function refused this session: ask for a code whatever the token says.
  if (force === "verify") return verified ? "verify" : "enroll";
  const step = mfaStep(aal?.currentLevel, verified);
  return step === "ok" ? "admin" : step;
}

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Blocks the entire platform admin UI under /:lang/admin/* (not just the sidebar link).
 * Matches edge functions: signed-in user email === platform superadmin (see platformAdmin.ts),
 * on a session that passed the authenticator-app code (aal2); otherwise the admin is
 * sent to set one up or to enter the code (AdminMfa).
 * Real enforcement: verifyAdmin() in Supabase edge functions.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { lang } = useParams<{ lang: string }>();
  const [state, setState] = useState<State>("loading");

  useEffect(() => {
    const apply = (force?: "verify") => {
      adminState(force)
        .then(setState)
        .catch(() => setState("denied"));
    };
    apply();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      apply();
    });
    const onMfaRequired = () => apply("verify");
    window.addEventListener(MFA_REQUIRED_EVENT, onMfaRequired);
    return () => {
      subscription.unsubscribe();
      window.removeEventListener(MFA_REQUIRED_EVENT, onMfaRequired);
    };
  }, []);

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (state === "denied") {
    return <Navigate to={`/${lang || "en"}/dashboard`} replace />;
  }

  if (state === "enroll" || state === "verify") {
    return (
      <LanguageRoute>
        <AdminMfa key={state} mode={state} onDone={() => setState("admin")} />
      </LanguageRoute>
    );
  }

  return <LanguageRoute>{children}</LanguageRoute>;
}
